/**
 * StoreBadges.jsx
 * ----------------
 * Official-style App Store + Google Play download badges.
 *
 * Uses inline SVGs that mirror Apple/Google brand guidelines
 * (dark pill, brand icon on left, "Download on the / App Store" or
 * "GET IT ON / Google Play" lockup on right).
 *
 * Since the native app is not yet published, badges render as visually-
 * finished but disabled buttons with a subtle "قريبًا" (coming soon) hint
 * on hover — improving trust while remaining truthful.
 */
import React from "react";

const AppleLogo = ({ size = 22 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 384 512"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const GooglePlayLogo = ({ size = 22 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 512 512"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <path
      fill="#EA4335"
      d="M325.3 234.3L104.5 13.8 393.2 172c25.8 14.1 25.5 51 0 65.5l-67.9 37.8-58-52.3z"
    />
    <path
      fill="#FBBC04"
      d="M104.5 498.2l220.8-220.7 58 52.3-289 158.7c-15.2 8.5-33.8 8.5-49-.2z"
    />
    <path
      fill="#4285F4"
      d="M104.5 13.8v484.4c-.5-.3-.9-.5-1.4-.8C88 489.7 79 474.3 79 458.7V53.6c0-15.6 9-31 24.1-38.9.5-.3.9-.6 1.4-.9z"
    />
    <path
      fill="#34A853"
      d="M267.3 256l-162.8 162.7c-.5-.2-.9-.5-1.4-.7L267.3 256z"
    />
  </svg>
);

/** Single badge — dark pill w/ 2-line lockup. */
const Badge = ({ testid, icon, small, large, disabled = true, srLabel }) => (
  <button
    type="button"
    aria-label={srLabel}
    aria-disabled={disabled}
    data-testid={testid}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      background: "#000",
      color: "#fff",
      border: "1px solid #2a2640",
      borderRadius: 10,
      padding: "8px 16px",
      minWidth: 168,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: 1,
      textAlign: "start",
      fontFamily: "inherit",
      transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
      position: "relative",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.borderColor = "#c79c6a";
      e.currentTarget.style.boxShadow = "0 8px 24px rgba(199,156,106,.18)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.borderColor = "#2a2640";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    <span style={{ display: "inline-flex", alignItems: "center", color: "#fff" }}>
      {icon}
    </span>
    <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
      <span
        style={{
          fontSize: 10,
          opacity: 0.75,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        {small}
      </span>
      <span style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>
        {large}
      </span>
    </span>
    {/* "قريبًا" hint pill */}
    <span
      style={{
        position: "absolute",
        top: -8,
        insetInlineEnd: 8,
        background: "#c79c6a",
        color: "#0e1430",
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 100,
        letterSpacing: 0.5,
      }}
    >
      قريبًا
    </span>
  </button>
);

export default function StoreBadges({ size = "md", align = "center" }) {
  const justify =
    align === "start" ? "flex-start" : align === "end" ? "flex-end" : "center";
  return (
    <div
      data-testid="store-badges"
      style={{
        display: "flex",
        gap: 12,
        justifyContent: justify,
        flexWrap: "wrap",
        marginTop: size === "sm" ? 8 : 14,
      }}
    >
      <Badge
        testid="badge-app-store"
        icon={<AppleLogo size={26} />}
        small="Download on the"
        large="App Store"
        srLabel="Download on the App Store (coming soon)"
      />
      <Badge
        testid="badge-google-play"
        icon={<GooglePlayLogo size={24} />}
        small="GET IT ON"
        large="Google Play"
        srLabel="Get it on Google Play (coming soon)"
      />
    </div>
  );
}
